import React from 'react';

import styles from './ErrorBoundary.module.scss';

/**
 * The admin console had no error boundary at all.
 *
 * That is not a cosmetic gap. Since React 16, an error thrown during render or
 * in a lifecycle method that reaches the root **unmounts the entire component
 * tree** -- React's deliberate choice, on the grounds that a corrupted UI is
 * worse than no UI. With nothing to catch it, the admin turned completely
 * white: no message, no navigation, no way back except knowing to reload.
 *
 * `<Suspense>` in App.tsx does not help. Suspense catches a component
 * *suspending*, which is a different signal from a component *throwing*.
 *
 * The realistic trigger is a shape mismatch, not exotic code. Every list page
 * maps over data from the API, so one endpoint returning a field the table
 * dereferences -- a null `doctor` on a visit row, a provider with no
 * `patient.name` -- takes the whole console down rather than one table.
 *
 * What this deliberately does NOT do is retry by itself. The error is already
 * in the rendered output at that point; re-rendering the same broken state in
 * a loop is how a blank page becomes a spinning one. The reload button is a
 * hard `location.reload()`, which is the only reliable way back to a known
 * state.
 */

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  // Class component on purpose: `getDerivedStateFromError` and
  // `componentDidCatch` have no hook equivalent. This is the one case React
  // still has no function-component API for.
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Kept as console.error rather than swallowed. Without it the stack is
    // gone -- React logs its own copy in development, but a production build
    // does not, and this is the only record an admin can be asked to paste
    // from their browser console.
    console.error('Admin console crashed:', error, info.componentStack);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div className={styles.wrapper} role="alert">
        <h1 className={styles.title}>Something broke on this page</h1>
        <p className={styles.body}>
          The console hit an error it could not recover from. Your data is not
          affected — nothing was saved or changed by this.
        </p>
        <p className={styles.detail}>{this.state.error.message}</p>
        <button
          className={styles.button}
          type="button"
          onClick={() => window.location.reload()}
        >
          Reload the page
        </button>
      </div>
    );
  }
}
